import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Card,
  CardBody,
  CardHeader,
  DataTable,
  EmptyState,
  formatDate,
} from "@odingard/design-system";
import type {
  DownloadedArtifact,
  ReportArtifact,
  ReportDefinition,
  ReportDownload,
} from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, governanceApi } from "../../lib/api";
import { GOVERNANCE_PERMISSIONS, GovPanel, GovernedAction } from "../../governance";
import {
  RunStatusBadge,
  duration,
  formatLabel,
  reportTypeDescription,
  reportTypeLabel,
  triggerLabel,
} from "./reportLabels";

interface Member {
  id: string;
  email: string;
  displayName: string;
}

/** Hands the bytes the Worker returned to the browser under their own name. */
function save(artifact: DownloadedArtifact): void {
  const url = URL.createObjectURL(artifact.blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = artifact.filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  URL.revokeObjectURL(url);
}

// One report run (APP-004 R3 P6).
//
// The run is the receipt: what was asked for, what was measured, the hash of
// the measurement, and every copy that has left the platform since. Nothing
// here is recalculated in the browser.
export function GovReportRun() {
  const { runId = "" } = useParams();
  const { activeOrg } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [taken, setTaken] = useState<DownloadedArtifact | null>(null);

  const state = useAsync(async () => (await governanceApi.reportRun(orgId, runId)).data, [
    orgId,
    runId,
  ]);
  const members = useAsync<Member[]>(async () => (await api.members(orgId)).members, [orgId]);
  const memberName = (id: string) => {
    const match = (members.data ?? []).find((m) => m.id === id);
    return match ? match.displayName || match.email : id;
  };

  const definitionId = state.data?.run.definitionId ?? null;
  const definition = useAsync<ReportDefinition | null>(
    async () =>
      definitionId ? (await governanceApi.reportDefinition(orgId, definitionId)).data.reportDefinition : null,
    [orgId, definitionId],
  );

  const run = state.data?.run;
  const artifacts = state.data?.artifacts ?? [];
  const parameters = Object.entries(run?.parameters ?? {});

  return (
    <>
      <div className="page-head">
        <div>
          <p className="muted-note">
            <Link to="/studio/governance/reports">Reports</Link>
          </p>
          <h1>{run ? reportTypeLabel(run.reportType) : "Report run"}</h1>
          <p>{run ? reportTypeDescription(run.reportType) : "Loading this run…"}</p>
        </div>
      </div>

      <GovPanel title="Run" state={state} loadingLabel="Loading this run…">
        {(data) => (
          <>
            <dl className="detail-grid" data-testid="run-detail">
              <div>
                <dt>Status</dt>
                <dd>
                  <RunStatusBadge status={data.run.status} />
                  {data.run.error ? <div className="muted-note">{data.run.error}</div> : null}
                </dd>
              </div>
              <div>
                <dt>Definition</dt>
                <dd>{definition.data ? definition.data.name : "Run without a definition"}</dd>
              </div>
              <div>
                <dt>Trigger</dt>
                <dd>{triggerLabel(data.run.trigger)}</dd>
              </div>
              <div>
                <dt>Requested by</dt>
                <dd>{memberName(data.run.requestedBy)}</dd>
              </div>
              <div>
                <dt>Started</dt>
                <dd>{formatDate(data.run.startedAt)}</dd>
              </div>
              <div>
                <dt>Completed</dt>
                <dd>
                  {data.run.completedAt ? formatDate(data.run.completedAt) : "Still running"}
                </dd>
              </div>
              <div>
                <dt>Duration</dt>
                <dd>{duration(data.run.durationMs)}</dd>
              </div>
              <div>
                <dt>Rows measured</dt>
                <dd>{data.run.rowCount}</dd>
              </div>
              <div>
                <dt>Input hash</dt>
                <dd>
                  {data.run.inputsHash ? (
                    <code data-testid="inputs-hash">{data.run.inputsHash}</code>
                  ) : (
                    <span className="muted-note">Nothing was measured</span>
                  )}
                </dd>
              </div>
              <div>
                <dt>Recipients</dt>
                <dd>
                  {definition.data && definition.data.recipients.length > 0
                    ? definition.data.recipients.map(memberName).join(", ")
                    : "Nobody notified"}
                </dd>
              </div>
            </dl>

            {parameters.length > 0 ? (
              <DataTable
                caption="Parameters"
                columns={[
                  { key: "name", header: "Parameter", render: (row) => row.id },
                  { key: "value", header: "Value", render: (row) => row.value },
                ]}
                rows={parameters.map(([name, value]) => ({ id: name, value: String(value) }))}
              />
            ) : (
              <p className="muted-note">No parameters. The report covers the whole organization.</p>
            )}
          </>
        )}
      </GovPanel>

      <Card>
        <CardHeader title="How to read this run" />
        <CardBody>
          <ul className="plain-list">
            <li>
              The input hash covers the report type, the parameters and every figure measured. Two
              runs over an unchanged governance position carry the same hash, so a figure quoted in
              one copy can be reconciled with another.
            </li>
            <li>
              Readiness scores come from frozen audit snapshots. Reporting reads them and never
              recalculates them.
            </li>
            <li>
              AI confidence is informational. It never changes a status, a classification or a
              readiness score, and an unverified AI mapping counts as uncovered.
            </li>
          </ul>
        </CardBody>
      </Card>

      <Card>
        <CardHeader title="Artifacts" />
        <CardBody>
          <p className="muted-note">
            Each rendered file, with the hash of its content at the moment it was produced.
          </p>
          {artifacts.length === 0 ? (
            <EmptyState
              title="No artifacts"
              description="This run produced no file. A failed run keeps its error rather than a partial export."
            />
          ) : (
            <div className="stack">
              {artifacts.map((artifact) => (
                <Artifact
                  key={artifact.id}
                  orgId={orgId}
                  artifact={artifact}
                  memberName={memberName}
                  onDownloaded={setTaken}
                />
              ))}
            </div>
          )}
          {taken ? (
            <p className="muted-note" data-testid="last-download">
              Downloaded {taken.filename} as {taken.mediaType}
              {taken.contentHash ? ` with content hash ${taken.contentHash}` : ""}.
            </p>
          ) : null}
        </CardBody>
      </Card>
    </>
  );
}

function Artifact({
  orgId,
  artifact,
  memberName,
  onDownloaded,
}: {
  orgId: string;
  artifact: ReportArtifact;
  memberName: (id: string) => string;
  onDownloaded: (artifact: DownloadedArtifact) => void;
}) {
  const downloads = useAsync<ReportDownload[]>(
    async () => (await governanceApi.artifactDownloads(orgId, artifact.id)).data.downloads,
    [orgId, artifact.id],
  );

  return (
    <div className="gov-framework">
      <div className="gov-framework__head">
        <div>
          <strong>{formatLabel(artifact.format)}</strong>
          <div className="muted-note">
            {artifact.mediaType} · {artifact.byteSize} bytes · rendered by {artifact.renderer} ·{" "}
            {formatDate(artifact.createdAt)}
          </div>
          <div className="muted-note">
            Content hash <code>{artifact.contentHash}</code>
          </div>
        </div>
        <GovernedAction
          permission={GOVERNANCE_PERMISSIONS.reportsExport}
          label={`Download ${formatLabel(artifact.format)}`}
          title={`Download ${formatLabel(artifact.format)}`}
          description="Takes a copy of this artifact exactly as it was rendered."
          consequence="Every copy is recorded against you, so a file shared outside the platform can be traced back."
          confirmLabel="Download"
          busyLabel="Downloading…"
          onConfirm={async () => {
            const file = await governanceApi.downloadArtifact(orgId, artifact.id);
            save(file);
            onDownloaded(file);
            return file;
          }}
          onDone={downloads.reload}
        />
      </div>
      <GovPanel
        title="Download history"
        state={downloads}
        loadingLabel="Loading download history…"
        isEmpty={(data) => data.length === 0}
        emptyTitle="No copies taken"
        emptyDescription="Nobody has downloaded this artifact yet."
      >
        {(data) => (
          <DataTable
            caption="Download history"
            columns={[
              {
                key: "actor",
                header: "Taken by",
                render: (row: ReportDownload) => memberName(row.actorId),
              },
              {
                key: "at",
                header: "When",
                render: (row: ReportDownload) => formatDate(row.downloadedAt),
              },
              {
                key: "hash",
                header: "Content hash",
                render: (row: ReportDownload) => <code>{row.contentHash.slice(0, 16)}…</code>,
              },
              { key: "size", header: "Bytes", render: (row: ReportDownload) => row.byteSize },
            ]}
            rows={data}
          />
        )}
      </GovPanel>
    </div>
  );
}
